import { memo, useCallback, useState } from 'react';

import { merge } from '@/components';
import { Button, Input, Separator, Slider, Text, TextField } from '@/components/core';
import { findField, FormatterValidatorHeader, ValidationFormSettings } from '@/components/ui';
import { useDebounceCallback, useValidatorMutation } from '@/hooks';
import { useModelConfigStore } from '@/store';
import { isNumber, parseNumber } from '@/utils';

export type ConfidenceValidatorProps = {
  fieldId: string;
  validatorId: string;
};

const IConfidenceValidator = ({ fieldId, validatorId }: ConfidenceValidatorProps) => {
  const field = useModelConfigStore((state) => findField(state.fields, fieldId));
  const validator = field?.validators?.find((v) => v.id === validatorId);
  const mutate = useValidatorMutation({ fieldId, validatorId, parentId: field?.parentId });
  const [threshold, setThreshold] = useState<number>(Number(validator?.config.thresholds?.automate ?? 0.9) * 100);

  const updateThresholdDebounced = useDebounceCallback(
    useCallback(
      (automate: number) => {
        const parsedAutomate = parseNumber(automate, (value) => value >= 0 && value <= 1);
        if (!isNumber(parsedAutomate)) return;

        mutate((validator) => {
          validator.name = `Confidence > ${(parsedAutomate * 100).toFixed(0)}%`;
          validator.config.thresholds = { automate: parsedAutomate };
        });
      },
      [mutate]
    ),
    400
  );

  const choices = [0, 0.65, 0.97, 1.0];
  const currentChoice = choices.findIndex((choice) => validator?.config.thresholds?.automate === choice);
  const [isCustomThreshold, setIsCustomThreshold] = useState(currentChoice === -1);

  const onUpdateThreshold = useCallback(
    (threshold: string | number | number[]) => {
      setThreshold(Number(threshold));
      updateThresholdDebounced(Number(threshold) / 100);
    },
    [updateThresholdDebounced]
  );

  if (!validator) return null;

  return (
    <div className="flex flex-col gap-4 p-4">
      <FormatterValidatorHeader validator={validator} />
      <Separator orientation="horizontal" />
      <div className="flex flex-col gap-4">
        <Text>
          Set the minimum AI confidence score needed to auto-approve this field. AI predictions below this threshold
          will be sent for human review.
        </Text>
        <div className="flex shrink">
          <div className="grid w-full grid-cols-5 rounded-lg border border-gray-200 p-0">
            {choices.map((choice, i) => (
              <Button
                className={merge(currentChoice === i && !isCustomThreshold && 'bg-gray-100', 'grow')}
                key={choice}
                onPress={() => {
                  setIsCustomThreshold(false);
                  onUpdateThreshold(choice * 100);
                }}
                variant="plain"
              >
                <Text>{Math.ceil(choice * 100)}%</Text>
              </Button>
            ))}
            <Button
              className={merge(isCustomThreshold && 'bg-gray-100')}
              onPress={() => setIsCustomThreshold(true)}
              variant="plain"
            >
              <Text>Custom</Text>
            </Button>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <Slider
            className="w-full"
            value={threshold}
            minValue={0}
            maxValue={100}
            step={1}
            onChange={onUpdateThreshold}
          />

          <TextField
            value={threshold.toFixed(0).toString()}
            onChange={onUpdateThreshold}
            defaultValue={threshold.toString()}
          >
            <Input type="number" step={1} min={0} max={100} data-testid="confidence-input" />
          </TextField>
        </div>
      </div>

      <Separator orientation="horizontal" />
      <ValidationFormSettings
        fieldId={fieldId}
        validatorId={validatorId}
        placeholderMessage="The AI is uncertain, please review this field."
      />
    </div>
  );
};

export const ConfidenceValidator = memo(IConfidenceValidator) as typeof IConfidenceValidator;
